import CampaignRepository from "../data/CampaignRepository";
import ChatRepository from "../data/ChatRepository";
import DocumentRepository from "../data/DocumentRepository";
import DocumentTemplateRepository from "../data/DocumentTemplateRepository";
import PaymentRepository from "../data/PaymentRepository";
import SignatoryRepository from "../data/SignatoryRepository";
import { PostComment } from "../entities/Chat";
import Document from "../entities/Document";
import DocumentTemplate from "../entities/DocumentTemplate";
import Payment from "../entities/Payment";
import Signatory, { SignatoryLogin } from "../entities/Signatory";
import User from "../entities/User";

export default class SignatoryInteractor {
  private documentRepository: DocumentRepository;
  private documentTemplateRepository: DocumentTemplateRepository;
  private signatoryRepository: SignatoryRepository;
  private campaignRepository: CampaignRepository;
  private chatRepository: ChatRepository;
  private paymentRepository: PaymentRepository;

  constructor(
    documentRepository: DocumentRepository,
    documentTemplateRepository: DocumentTemplateRepository,
    signatoryRepository: SignatoryRepository,
    campaignRepository: CampaignRepository,
    chatRepository: ChatRepository,
    paymentRepository: PaymentRepository,
  ) {
    this.documentRepository = documentRepository;
    this.documentTemplateRepository = documentTemplateRepository;
    this.signatoryRepository = signatoryRepository;
    this.campaignRepository = campaignRepository;
    this.chatRepository = chatRepository;
    this.paymentRepository = paymentRepository;
  }

  public async saveDocument(document: Document): Promise<Document> {
    return this.documentRepository.saveDocument(document);
  }

  public async saveDocumentRecord(document: Document): Promise<Document> {
    return this.documentRepository.saveDocumentRecord(document);
  }

  public async saveDocumentAction(document: Document): Promise<Document> {
    return this.documentRepository.saveDocumentAction(document);
  }

  public async getDocument(
    organizationId: string,
    documentId: string
  ): Promise<Document | undefined> {
    return this.documentRepository.getDocument(organizationId, documentId);
  }

  public async getDocumentExt(shortId: string): Promise<Document | undefined> {
    return this.documentRepository.getDocumentExt(shortId);
  }

  public async signDocument(
    user: User | Signatory,
    organizationId: string,
    documentId: string,
    signature: string,
    formData: string
  ): Promise<string | undefined> {
    return this.documentRepository.signDocument(
      user,
      organizationId,
      documentId,
      signature,
      formData
    );
  }

  public async resendDocument(
    user: User,
    documentId: string
  ): Promise<{ documentId: string; success: boolean; message?: string }> {
    return this.documentRepository.resendDocument(user, documentId);
  }

  public async sendBackDocument(
    user: User,
    documentId: string,
    reason: string
  ): Promise<boolean> {
    return this.documentRepository.sendBackDocument(user, documentId, reason);
  }

  public async saveDocumentProgress(
    organizationId: string,
    documentId: string,
    attachmentsString: string,
    signature: string
  ): Promise<boolean> {
    return this.documentRepository.saveDocumentProgress(organizationId, documentId, attachmentsString, signature);
  }

  public async externalSignDocument(
    shortId: string,
    signature: string,
    formData: string
  ): Promise<string | undefined> {
    return this.documentRepository.externalSignDocument(
      shortId,
      signature,
      formData
    );
  }

  public async getSignatoryDocuments(
    orgnizationId: string,
    signatoryId: string,
    downloadFile: boolean,
  ): Promise<Document[]> {
    return this.documentRepository.getSignatoryDocuments(
      orgnizationId,
      signatoryId,
      downloadFile
    );
  }

  public async getRequestorDocuments(
    orgnizationId: string,
    requestorId: string
  ): Promise<Document[]> {
    return this.documentRepository.getRequestorDocuments(
      orgnizationId,
      requestorId
    );
  }

  public async loadDocTemplateByCampaign(
    userId: string,
    organizationId: string,
    campaignId: string
  ): Promise<DocumentTemplate | undefined> {
    return this.documentTemplateRepository.loadDocTemplateByCampaign(
      userId,
      organizationId,
      campaignId
    );
  }

  public async saveSignatory(signatory: Signatory): Promise<Signatory> {
    return this.signatoryRepository.saveSignatory(signatory);
  }

  public async saveLanguageSignatory(language: string, signatoryEmail: string): Promise<Signatory> {
    return this.signatoryRepository.saveLanguageSignatory(language, signatoryEmail);
  }

  public async getSignatoryByShortId(shortid: string): Promise<Signatory> {
    return this.signatoryRepository.getSignatoryByShortId(shortid);
  }

  public async getSignatoryByShortIdThenVerify(shortId: string, password: string): Promise<SignatoryLogin> {
    return this.signatoryRepository.getSignatoryByShortIdThenVerify(shortId, password);
  }

  public async getSignatoryByEmail(email: string): Promise<Signatory> {
    return this.signatoryRepository.getSignatoryByEmail(email);
  }

  public async sendNewPin(shortId: string, forceResend?: boolean, email?: string): Promise<boolean> {
    return this.signatoryRepository.sendNewPin(shortId, forceResend, email);
  }

  public async translatePDF(
    documentTemplateId: string,
    language: string
  ): Promise<string> {
    return this.documentTemplateRepository.translatePDF(
      documentTemplateId,
      language
    );
  }

  public async saveChat(
    campaignId: string,
    signatoryEmail: string,
    postComment: PostComment
  ): Promise<PostComment> {
    return this.chatRepository.saveChat(
      campaignId,
      signatoryEmail,
      postComment
    );
  }

  public async getChats(
    campaignId: string,
    signatoryEmail: string
  ): Promise<PostComment[]> {
    return this.chatRepository.getChats(campaignId, signatoryEmail);
  }

  public async getChatsExt(shortId: string): Promise<PostComment[]> {
    return this.chatRepository.getChatsExt(shortId);
  }

  public async getAllChats(
    campaignIds: string[],
    signatoryEmail: string
  ): Promise<PostComment[]> {
    return this.chatRepository.getAllChats(campaignIds, signatoryEmail);
  }

  public async deleteChat(
    campaignId: string,
    signatoryEmail: string,
    chatId: string
  ): Promise<Boolean> {
    return this.chatRepository.deleteChat(campaignId, signatoryEmail, chatId);
  }

  public async savePayment(payment: Payment): Promise<Payment> {
    return this.paymentRepository.savePayment(payment);
  }

  
  public async successPayment(
    paymentId: string,
  ): Promise<Payment> {
    return this.paymentRepository.successPayment(paymentId);
  }

  public async getPayment(paymentId: string): Promise<Payment> {
    return this.paymentRepository.getPayment(paymentId);
  }

  public async getPayments(organizationId: string): Promise<Payment[]> {
    return this.paymentRepository.getPayments(organizationId);
  }

  public async getPaymentsLookupsCount(
    user: User,
    lookupType: string,
    lookupValue: string,
    statusFilter?: string,
    dateRange?: string
  ): Promise<number> {
    return this.paymentRepository.getPaymentsLookupsCount(
      user,
      lookupType,
      lookupValue,
      statusFilter,
      dateRange
    );
  }

  public async getPaymentsByLookupPagination(
    user: User,
    lookupType: string,
    lookupValue: string,
    pageSize: number,
    lastEvaluatedKey?: any,
    existingMessageIds?: any,
    statusFilter?: string,
    dateRange?: string
  ): Promise<{ items: Payment[]; lastEvaluatedKey?: any }> {
    return this.paymentRepository.getPaymentsByLookupPagination(
      user,
      lookupType,
      lookupValue,
      pageSize,
      lastEvaluatedKey,
      existingMessageIds,
      statusFilter,
      dateRange
    );
  }

  public async downloadPDF(
    organizationId: string,
    userId: string,
    documentId: string
  ): Promise<string> {
    return this.documentRepository.downloadPDF(
      organizationId,
      userId,
      documentId
    );
  }

  public async downloadPDFwithAuditSummary(
    organizationId: string,
    userId: string,
    documentId: string
  ): Promise<{ signedDocumentPdf: string; auditSummaryPdf: string }> {
    return this.documentRepository.downloadPDFwithAuditSummary(
      organizationId,
      userId,
      documentId
    );
  }

  public async saveAttachment(
    documentId: string,
    attachmentId: string,
    attachmentFile: string
  ): Promise<string> {
    return this.documentRepository.saveAttachment(
      documentId,
      attachmentId,
      attachmentFile
    );
  }

  public async getAttachment(
    documentId: string,
    attachmentId: string
  ): Promise<string> {
    return this.documentRepository.getAttachment(
      documentId,
      attachmentId
    );
  }
}
